import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Heart, 
  Square, 
  Clock, 
  Bed, 
  Bath, 
  Check, 
  Bookmark, 
  Building
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ui/toast/use-toast';

const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

const PropertyBadge = ({ type }) => {
  const badgeConfig = {
    alquiler: {
      bg: 'bg-blue-500',
      hover: 'group-hover:bg-blue-600',
      text: 'Alquiler'
    },
    comprar: {
      bg: 'bg-purple-500',
      hover: 'group-hover:bg-purple-600',
      text: 'Venta'
    }
  };

  const config = badgeConfig[type] || badgeConfig.comprar;

  return (
    <div className="absolute top-4 left-4 z-20">
      <div className={`px-3 py-1 text-sm font-medium rounded-lg shadow-md
                    transition-all duration-300
                    ${config.bg} ${config.hover}
                    text-white`}
      >
        {config.text}
      </div>
    </div>
  );
};

const PropertyInfo = ({ icon: Icon, children, className = "" }) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <Icon className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
    <span className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300 group-hover:text-gray-800 dark:group-hover:text-gray-200">
      {children}
    </span>
  </div>
);

const FavoriteButton = ({ isFavorite, isLoading, onClick }) => (
  <button 
    className={`absolute top-4 right-4 z-20 p-2 rounded-full shadow-lg 
              transition-all duration-300 transform
              ${isLoading 
                ? 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed' 
                : isFavorite 
                  ? 'bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 hover:scale-110' 
                  : 'bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 hover:scale-110'
              } ${!isLoading && 'active:scale-95'}`}
    onClick={onClick}
    disabled={isLoading}
    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
  >
    <Heart 
      className={`w-5 h-5 transition-all duration-300
                ${isFavorite 
                  ? 'text-red-500 dark:text-red-400 fill-current' 
                  : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}
    />
  </button>
);

const PropertyImage = React.memo(({ url, alt, className }) => {
  const [error, setError] = useState(false);

  const getFullUrl = (imageUrl) => {
    if (!imageUrl) return '/default-property-image.jpg';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${CLOUDFLARE_PROPERTIES_URL}/${imageUrl}`;
  };

  const handleError = (e) => {
    console.error('Error loading image:', url);
    setError(true);
    e.target.src = '/default-property-image.jpg';
  };

  if (!url) {
    return (
      <div className={`${className} bg-gray-200 dark:bg-gray-700 flex items-center justify-center`}>
        <Building className="w-12 h-12 text-gray-400 dark:text-gray-500" />
      </div>
    );
  }

  return (
    <img
      src={error ? '/default-property-image.jpg' : getFullUrl(url)}
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
});

PropertyImage.displayName = 'PropertyImage';

const PriceTag = ({ amount, currency = "S/", emphasized = false, size = "normal" }) => {
  const sizeClasses = {
    small: "text-base",
    normal: "text-lg",
    large: "text-xl"
  };
  
  return (
    <div className={`font-bold ${emphasized ? 'text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300' : 'text-gray-700 dark:text-gray-300'} 
                   ${sizeClasses[size] || sizeClasses.normal} transition-colors duration-300`}>
      {currency} {amount}
    </div>
  );
};

const PropertyCard = ({ 
  property, 
  onFavoriteUpdate,
  disableNavigation = false,
  isSelectable = false,
  isSelected = false,
  onSelect
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [favoriteChecked, setFavoriteChecked] = useState(false);

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 30) {
      const diffMonths = Math.floor(diffDays / 30);
      return `${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`;
    } else if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minutos'}`;
    } else {
      return 'un momento';
    }
  };

  // Determinar el tipo correcto basado en la operación
  const getTipoElemento = (operacion) => {
    return operacion === 'alquiler' ? 'alquiler' : 'propiedad';
  };

  // Verificar estado de favorito
  const checkFavoriteStatus = async () => {
    if (!user || !property.id || favoriteChecked) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const tipo = getTipoElemento(property.operacion);
      const response = await fetch(
        `https://backend-production-1786.up.railway.app/api/favoritos/check/${property.id}?tipo=${tipo}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const { isFavorite: favoriteStatus } = await response.json();
        setIsFavorite(favoriteStatus);
        setFavoriteChecked(true);
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
      setFavoriteChecked(true);
    }
  };

  // Efecto para verificar estado inicial
  useEffect(() => {
    // Primero establecer desde las props si están disponibles
    if (property.isFavorite !== undefined) {
      setIsFavorite(property.isFavorite);
    }
    
    // Luego verificar con el servidor si el usuario está logueado
    if (user) {
      checkFavoriteStatus();
    } else {
      setFavoriteChecked(true);
    }
  }, [property.id, property.isFavorite, user]);

  const formatPrice = (price, currency = 'PEN') => {
    try {
      const numericPrice = Number(price) || 0;
      return new Intl.NumberFormat(currency === 'PEN' ? 'es-PE' : 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numericPrice).replace('S/', '').trim();
    } catch (error) {
      console.error('Price formatting error:', { price, error });
      return currency === 'PEN' ? '0' : '$0';
    }
  };

  const mainImage = Array.isArray(property.imagenes) && property.imagenes.length > 0
    ? property.imagenes[0]
    : null;

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: "Inicia sesión para guardar favoritos",
        description: "Necesitas una cuenta para guardar propiedades en favoritos",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No hay token disponible');

      const tipo = getTipoElemento(property.operacion);
      
      const response = await fetch(
        `https://backend-production-1786.up.railway.app/api/favoritos/${property.id}?tipo=${tipo}`, 
        {
          method: isFavorite ? 'DELETE' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al actualizar favoritos');
      }

      const newFavoriteStatus = !isFavorite;
      setIsFavorite(newFavoriteStatus);
      
      const tipoLabel = tipo === 'alquiler' ? 'El alquiler' : 'La propiedad';
      
      toast({
        title: newFavoriteStatus ? "Añadido a favoritos" : "Eliminado de favoritos",
        description: newFavoriteStatus 
          ? `${tipoLabel} se añadió a tus favoritos` 
          : `${tipoLabel} se eliminó de tus favoritos`,
      });

      if (onFavoriteUpdate) {
        onFavoriteUpdate(property.id, newFavoriteStatus);
      }

    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar favoritos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (disableNavigation) {
      if (isSelectable && onSelect) {
        onSelect(property);
      }
      return;
    }
  
    const basePath = property.operacion === 'alquiler' ? '/alquiler' : '/propiedad';
    navigate(`${basePath}/${property.id}`);
  };

  return (
    <Card
      className={`group relative overflow-hidden cursor-pointer bg-white dark:bg-gray-800 h-full
                transform transition-all duration-300 ease-out
                hover:shadow-lg dark:hover:shadow-xl hover:-translate-y-1 focus:outline-none
                ${isSelected ? 'ring-2 ring-blue-500 dark:ring-blue-400' : 'hover:border-blue-200 dark:hover:border-blue-600'}
                border border-gray-200 dark:border-gray-600 rounded-xl`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
    >
      {/* Imagen y badges */}
      <div className="relative h-52 overflow-hidden">
        {/* Overlay gradiente superior */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent z-10" />
        
        <PropertyImage
          url={mainImage}
          alt={property.titulo}
          className="w-full h-full object-cover transition-transform duration-500 ease-out
                   group-hover:scale-110"
        />
        
        <PropertyBadge type={property.operacion} />
        
        {/* Mostrar botón de favorito solo cuando esté cargado el estado */}
        {favoriteChecked && (
          <FavoriteButton 
            isFavorite={isFavorite}
            isLoading={isLoading}
            onClick={handleFavoriteClick}
          />
        )}
        
        {/* Fecha relativa */}
        <div className="absolute bottom-3 left-4 z-10 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-md text-xs">
          <Clock className="w-3 h-3" />
          <span>Hace {getRelativeTime(property.fecha_publicacion)}</span>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="p-4 flex flex-col h-[calc(100%-13rem)]">
        {/* Ubicación */}
        <div className="mb-2">
          <PropertyInfo icon={MapPin} className="text-gray-500 dark:text-gray-400">
            <span title={property.ubicacion}>
              {property.ubicacion && property.ubicacion.length > 30 
                ? `${property.ubicacion.slice(0, 27)}...` 
                : property.ubicacion || 'Ubicación no especificada'}
            </span>
          </PropertyInfo>
        </div>

        {/* Título */}
        <h3 className="font-semibold text-gray-800 dark:text-gray-200 transition-colors duration-300 
                      group-hover:text-blue-700 dark:group-hover:text-blue-400 line-clamp-2 mb-3" 
            title={property.titulo}>
          {property.titulo || 'Propiedad sin título'}
        </h3>

        {/* Características principales */}
        <div className="flex items-center gap-3 mb-4">
          {property.area_total && (
            <div className="flex items-center justify-center p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <PropertyInfo icon={Square}>
                {property.area_total}m²
              </PropertyInfo>
            </div>
          )}
          
          {property.habitaciones && (
            <div className="flex items-center justify-center p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <PropertyInfo icon={Bed}>
                {property.habitaciones} hab
              </PropertyInfo>
            </div>
          )}
          
          {property.banos && (
            <div className="flex items-center justify-center p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <PropertyInfo icon={Bath}>
                {property.banos} baños
              </PropertyInfo>
            </div>
          )}
        </div>

        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700">
          {/* Precios */}
          <div className="flex items-end justify-between">
            <div>
              {property.operacion === 'alquiler' ? (
                <div className="flex items-center gap-1">
                  <PriceTag 
                    amount={formatPrice(property.precio_mensual)} 
                    emphasized={true} 
                    size="large"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">/mes</span>
                </div>
              ) : (
                <div className="space-y-1">
                  <PriceTag 
                    amount={formatPrice(property.precio_soles)} 
                    emphasized={true} 
                    size="large"
                  />
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatPrice(property.precio_dolares, 'USD')}
                  </div>
                </div>
              )}
            </div>
            
            {/* Vendedor */}
            {property.vendedor_nombre && (
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded-md">
                <Check className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[100px]" title={property.vendedor_nombre}>
                  {property.vendedor_nombre.length > 10
                    ? `${property.vendedor_nombre.slice(0, 8)}...`
                    : property.vendedor_nombre}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Selección indicator */}
      {isSelected && (
        <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-blue-500 dark:bg-blue-400 flex items-center justify-center shadow-md z-20">
          <Check className="w-5 h-5 text-white" />
        </div>
      )}
    </Card>
  );
};

export default PropertyCard;